import { useCuSystemAnalyzerClient, useSystemAnalyzerClient } from '@/api-proxy/useProxiedClient'
import { getAllVehiclesOptions } from '@/api/main/@tanstack/react-query.gen'
import ErrorState from '@/components/Common/ErrorState'
import PageShell from '@/components/Common/PageShell'
import { SectionTitle } from '@/components/Common/SectionTitle'
import { usePageTitle } from '@/hooks/usePageTitle'
import { CITY_NAME } from '@/meta'
import { Route } from '@/routes/diagnostics.$source.$tab'
import { Button, Group, LoadingOverlay, Select, Tabs } from '@mantine/core'
import { useQuery } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { SlArrowLeft } from 'react-icons/sl'
import { TbCar } from 'react-icons/tb'
import CameraInterfacesPanel from './CameraInterfacesPanel'
import CuLogDownloadsPanel from './CuLogDownloadsPanel'
import LogDownloadsPanel from './LogDownloadsPanel'
import LogsPanel from './LogsPanel'
import MetricsPanel from './MetricsPanel'
import SystemOverviewPanel from './SystemOverviewPanel'
import { isDiagnosticsTab } from './types'

const DiagnosticsPage = () => {
  const { t } = useTranslation(['diagnostics', 'common'])
  const { source, tab } = Route.useParams()
  const navigate = useNavigate()

  usePageTitle(`Diagnostics ${source} ${tab} | ${CITY_NAME}`)

  const {
    data: vehicles,
    isLoading: isLoadingVehicles,
    isError: isVehiclesError,
  } = useQuery({
    ...getAllVehiclesOptions(),
    refetchOnWindowFocus: false,
  })

  const client = useSystemAnalyzerClient(source)
  const cuClient = useCuSystemAnalyzerClient(source)

  const invalidTab = !isDiagnosticsTab(tab)

  if (isLoadingVehicles) return <LoadingOverlay visible />

  if (isVehiclesError || !Array.isArray(vehicles)) {
    return (
      <PageShell title={t('diagnostics')}>
        <ErrorState title={t('vehiclesFetchFailed')} message={t('vehiclesFetchFailedMessage')} />
      </PageShell>
    )
  }

  if (invalidTab) {
    return (
      <PageShell title={t('diagnostics')}>
        <ErrorState title={t('invalidTab')} message={t('invalidTabMessage')} />
      </PageShell>
    )
  }

  if (!vehicles.some(vehicle => vehicle?.id === source)) {
    return (
      <PageShell title={t('diagnostics')}>
        <ErrorState title={t('invalidSource')} message={t('invalidSourceMessage')} />
      </PageShell>
    )
  }

  if (!client || !cuClient) {
    return (
      <PageShell title={t('diagnostics')}>
        <ErrorState title={t('clientUnavailable')} message={t('clientUnavailableMessage')} />
      </PageShell>
    )
  }

  const vehicleOptions = vehicles
    .map(vehicle => ({ value: vehicle.id, label: vehicle.id.toUpperCase() }))
    .sort((a, b) => a.label.localeCompare(b.label))

  const title = (
    <Group justify="space-between" align="center" wrap="nowrap">
      <Group align="center" gap="md" wrap="nowrap">
        <SectionTitle order={1}>{t('diagnostics')}</SectionTitle>
        <Select
          mb="md"
          data={vehicleOptions}
          value={source}
          onChange={value => {
            if (!value || value === source) return
            navigate({ to: `/diagnostics/${value}/${tab}` })
          }}
          leftSection={<TbCar size={16} />}
          aria-label={t('selectVehicle')}
          placeholder={t('selectVehicle')}
          searchable
          allowDeselect={false}
          checkIconPosition="right"
          w={180}
        />
      </Group>
      <Button
        mb="md"
        leftSection={<SlArrowLeft />}
        onClick={() => navigate({ to: '/vehicles' })}
        size="sm"
        variant="subtle"
      >
        {t('back', { ns: 'common' })}
      </Button>
    </Group>
  )

  return (
    <PageShell title={title}>
      <Tabs
        value={tab}
        onChange={value => {
          if (!value) return
          navigate({ to: `/diagnostics/${source}/${value}` })
        }}
      >
        <Tabs.List grow={false}>
          <Tabs.Tab value="overview">{t('overview')}</Tabs.Tab>
          <Tabs.Tab value="camera-interfaces">{t('cameraInterfaces')}</Tabs.Tab>
          <Tabs.Tab value="citysense-car-logs">{t('citysenseCarLogs')}</Tabs.Tab>
          <Tabs.Tab value="system-logs">{t('systemLogs')}</Tabs.Tab>
          <Tabs.Tab value="log-downloads">{t('downloads')}</Tabs.Tab>
          <Tabs.Tab value="cs-metrics">{t('csMetrics')}</Tabs.Tab>
          <Tabs.Tab value="control-unit-logs">{t('controlUnitLogs')}</Tabs.Tab>
          <Tabs.Tab value="cu-system-logs">{t('cuSystemLogs')}</Tabs.Tab>
          <Tabs.Tab value="cu-log-downloads">{t('cuDownloads')}</Tabs.Tab>
          <Tabs.Tab value="cu-metrics">{t('cuMetrics')}</Tabs.Tab>
        </Tabs.List>
        {tab === 'overview' && <SystemOverviewPanel client={client} cuClient={cuClient} source={source} />}
        {tab === 'camera-interfaces' && <CameraInterfacesPanel client={client} source={source} />}
        {tab === 'citysense-car-logs' && (
          <LogsPanel value="citysense-car-logs" title={t('citysenseCarLogs')} client={client} source={source} />
        )}
        {tab === 'system-logs' && (
          <LogsPanel value="system-logs" title={t('systemLogs')} client={client} source={source} />
        )}
        {tab === 'log-downloads' && <LogDownloadsPanel client={client} />}
        {tab === 'cs-metrics' && <MetricsPanel value="cs-metrics" title={t('csMetrics')} source={source} />}
        {tab === 'control-unit-logs' && (
          <LogsPanel value="control-unit-logs" title={t('controlUnitLogs')} client={cuClient} source={source} />
        )}
        {tab === 'cu-system-logs' && (
          <LogsPanel value="cu-system-logs" title={t('cuSystemLogs')} client={cuClient} source={source} />
        )}
        {tab === 'cu-log-downloads' && <CuLogDownloadsPanel client={cuClient} />}
        {tab === 'cu-metrics' && <MetricsPanel value="cu-metrics" title={t('cuMetrics')} source={source} />}
      </Tabs>
    </PageShell>
  )
}

export default DiagnosticsPage
